import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const Hero = () => (
  <section
    className="relative flex min-h-screen items-center overflow-hidden grid-lines noise-overlay pt-[72px]"
    data-testid="hero-section"
  >
    <div
      className="pointer-events-none absolute left-1/2 top-1/3 h-[620px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[140px]"
      style={{
        background:
          "radial-gradient(circle, rgba(46,49,146,0.18) 0%, rgba(109,200,240,0.22) 45%, transparent 70%)",
      }}
    />

    <div className="relative mx-auto w-full max-w-4xl px-5 pb-24 pt-16 text-center sm:px-8 lg:pt-20">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.6 }}
        className="mb-8 flex flex-wrap items-center justify-center gap-3"
      >
        <span className="flex items-center gap-3">
          <span className="live-dot h-2 w-2 rounded-full bg-emerald-400" />
          <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500">
            Live across US · Canada · UK · Australia
          </span>
        </span>
      </motion.div>

      <h1
        data-testid="hero-headline-text"
        className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl"
      >
        <MaskedLine delay={0.25}>Live order management.</MaskedLine>
        <MaskedLine delay={0.4} className="text-[#2E3192]">
          Real-time design.
        </MaskedLine>
        <MaskedLine delay={0.55}>One dedicated team.</MaskedLine>
      </h1>

      <motion.p
        data-testid="hero-subheadline-text"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-slate-500 sm:text-lg"
      >
        Artwork360 runs your order desk and art department live, in your business
        hours — real-time order processing, vendor follow-ups, and print-ready
        graphic design for promotional products distributors. Your sales reps
        focus on winning more sales, not chasing follow-ups.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 flex flex-wrap items-center justify-center gap-4"
      >
        <button
          data-testid="hero-primary-cta"
          onClick={() => scrollToId("contact")}
          className="group flex items-center gap-2 rounded-full bg-[#2E3192] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#6DC8F0] hover:text-[#07090E]"
        >
          Get in Touch
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
        <button
          data-testid="hero-secondary-cta"
          onClick={() => scrollToId("pricing")}
          className="rounded-full border border-slate-300 px-7 py-3.5 text-sm font-semibold text-slate-700 transition-colors duration-300 hover:border-[#2E3192]/60 hover:text-[#2E3192]"
        >
          Request a Quote
        </button>
      </motion.div>
    </div>

    <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2">
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        className="h-9 w-px bg-gradient-to-b from-transparent via-[#2E3192]/50 to-transparent"
      />
    </div>
  </section>
);

export default Hero;
